import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Badge from '../common/Badge';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import { NativeSelect } from '../common/FormField';
import { SectionCard, Spinner } from '../common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { queryKeys } from '../../lib/queryClient';
import { api, errorMessage } from '../../services/api';
import { AppUser } from '../../types';
import { compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../../utils/validation';

const ROLES = [
  { value: 'ADMIN', label: 'Admin — full access' },
  { value: 'ACCOUNTANT', label: 'Accountant — everything except company profile & users' },
  { value: 'VIEWER', label: 'Viewer — read only' },
];

interface FormState {
  name: string;
  email: string;
  password: string;
  role: string;
  status: string;
}

export default function UsersTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user: me } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [form, setForm] = useState<FormState>({ name: '', email: '', password: '', role: 'ACCOUNTANT', status: 'active' });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.users(),
    queryFn: () => api.get<AppUser[]>('/users'),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => (editing ? api.patch(`/users/${editing.id}`, payload) : api.post('/users', payload)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users() }),
  });

  function open(u?: AppUser) {
    setEditing(u ?? null);
    setErrors({});
    setForm(u ? { name: u.name, email: u.email, password: '', role: u.role, status: u.status } : { name: '', email: '', password: '', role: 'ACCOUNTANT', status: 'active' });
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const email = form.email.trim().toLowerCase();
    const e = compact({
      name: v.personName(form.name, 'Name', true),
      email: editing
        ? undefined
        : v.email(form.email, true) ?? (users.some((u) => u.email.toLowerCase() === email) ? 'A user with this email already exists' : undefined),
      // Required for a new user; optional (but still checked) when editing.
      password: !editing || form.password ? v.password(form.password) : undefined,
      role: form.role ? undefined : 'Role is required',
    });
    setErrors(e);
    if (hasErrors(e)) return focusFirstError();

    const isSelf = editing?.id === me?.id;
    const payload: Record<string, unknown> = editing
      ? { name: form.name.trim(), ...(isSelf ? {} : { role: form.role, status: form.status }), ...(form.password ? { password: form.password } : {}) }
      : { name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role };
    try {
      await saveMutation.mutateAsync(payload);
      showToast(editing ? 'User updated' : 'User created', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, ['name', 'email', 'password', 'role', 'status'] as const));
      showToast(errorMessage(err, 'Failed to save user'), 'error');
    }
  }

  if (isLoading) return <Spinner />;
  const isSelf = editing?.id === me?.id;

  return (
    <>
      <SectionCard title={`Users (${users.length})`} bodyClassName="p-0"
        actions={<Button size="sm" variant="primary" icon="fa-user-plus" onClick={() => open()}>Add User</Button>}>
        <ul className="divide-y divide-ink-100">
          {users.map((u) => (
            <li key={u.id} className="flex items-center gap-3 px-4 py-3">
              <span className="w-9 h-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-sm shrink-0">
                {u.name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-900">
                  {u.name}
                  {u.id === me?.id && <span className="ml-2 text-xs font-normal text-ink-400">(you)</span>}
                </p>
                <p className="text-xs text-ink-500 truncate">{u.email}</p>
              </div>
              <Badge variant={u.role === 'ADMIN' ? 'primary' : u.role === 'ACCOUNTANT' ? 'info' : 'secondary'} size="sm">{u.role}</Badge>
              {u.status !== 'active' && <Badge variant="danger" size="sm">Inactive</Badge>}
              <button className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200 shrink-0" onClick={() => open(u)}>Edit</button>
            </li>
          ))}
        </ul>
      </SectionCard>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit User' : 'Add User'} size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        }>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate autoComplete="off">
          <Input label="Name" required value={form.name} maxLength={120} error={errors.name}
            onChange={(e) => setField('name', e.target.value.replace(/[^A-Za-z .'-]/g, ''))} />
          <Input label="Email" type="email" required disabled={!!editing} value={form.email} maxLength={255}
            onChange={(e) => setField('email', e.target.value.trim())} error={errors.email} autoComplete="off" />
          <Input label={editing ? 'New Password (leave blank to keep)' : 'Password'} type="password" required={!editing} maxLength={72}
            value={form.password} onChange={(e) => setField('password', e.target.value)} error={errors.password}
            helperText="8-72 characters with at least one letter and one number" autoComplete="new-password" />
          <NativeSelect label="Role" required value={form.role} error={errors.role}
            onChange={(val) => setField('role', val)} options={ROLES} disabled={isSelf} />
          {editing && !isSelf && (
            <NativeSelect label="Status" value={form.status} onChange={(val) => setField('status', val)}
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive — cannot sign in' }]} />
          )}
        </form>
      </Modal>
    </>
  );
}
